import React, { useState, useEffect } from "react";
import localScheduleData from "./scheduleData.json";
import "./program.css";

const ConferenceSchedule = () => {
  // State to hold the fetched schedule data
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleError, setScheduleError] = useState("");
  // Track the currently selected session (with its color) for modal popup
  const [selectedSession, setSelectedSession] = useState(null);

  // Define the same colors as in your CSS alternate background colors
  const sessionColors = [
    "#e3f2fd", // light blue
    "#fce4ec", // light pink
    "#e8f5e9", // light green
    "#fff3e0", // light orange
    "#ede7f6", // light purple
    "#f3e5f5", // light violet
  ];

  const isProduction = process.env.NODE_ENV === "production";
  const isTest = process.env.NODE_ENV === "test";
  const useRemoteScheduleInDev =
    process.env.REACT_APP_USE_REMOTE_SCHEDULE_IN_DEV === "true";
  const disableScheduleCache =
    process.env.REACT_APP_DISABLE_SCHEDULE_CACHE !== "false";
  const shouldUseRemoteSchedule =
    isProduction || isTest || useRemoteScheduleInDev;

  useEffect(() => {
    let isMounted = true;
    let activeController = null;

    if (!shouldUseRemoteSchedule) {
      setScheduleError("");
      setScheduleData(localScheduleData);
      return undefined;
    }

    const loadSchedule = async () => {
      setScheduleError("");
      setScheduleData(null);

      for (let attempt = 1; attempt <= 2; attempt += 1) {
        activeController = new AbortController();

        try {
          const scheduleUrl = new URL("https://conference.smdc.edu.pk/scheduleData.json");

          if (disableScheduleCache) {
            scheduleUrl.searchParams.set("_ts", `${Date.now()}`);
          }

          const headers = {
            Accept: "application/json",
          };

          const response = await fetch(scheduleUrl.toString(), {
            cache: disableScheduleCache ? "no-store" : "default",
            headers: disableScheduleCache
              ? {
                  ...headers,
                  "Cache-Control": "no-cache, no-store, must-revalidate",
                  Pragma: "no-cache",
                  Expires: "0",
                }
              : headers,
            signal: activeController.signal,
          });

          if (!response.ok) {
            throw new Error(`Schedule request failed with status ${response.status}`);
          }

          const data = await response.json();

          if (isMounted) {
            setScheduleData(data);
            setScheduleError("");
          }

          return;
        } catch (error) {
          if (activeController.signal.aborted) {
            return;
          }

          if (attempt === 2) {
            console.error("Error fetching schedule data:", error);

            if (isMounted) {
              setScheduleError(
                "Unable to load the latest schedule data from the server. Please refresh and try again."
              );
            }

            return;
          }
        }
      }
    };

    loadSchedule();

    return () => {
      isMounted = false;
      if (activeController) {
        activeController.abort();
      }
    };
  }, [disableScheduleCache, shouldUseRemoteSchedule]);

  // Show a loading state until data is fetched
  if (scheduleError) {
    return <div>{scheduleError}</div>;
  }

  if (!scheduleData) {
    return <div>Loading schedule data...</div>;
  }

  const { highlight, chapters = [], days = [] } = scheduleData;

  const handleOpenSession = (session, color) => {
    // Store the session along with the computed color
    setSelectedSession({ ...session, color });
  };

  const handleCloseModal = () => {
    setSelectedSession(null);
  };

  return (
    <div className="conference-schedule">
      <div className="conference-schedule-container">
        <div className="conference-highlight">
          <h1 className="main-h1">Program Schedule</h1>
          <p>
            <strong>Dates:</strong> {highlight.duration}
          </p>
          <p>
            <strong>Theme:</strong> {highlight.theme}
          </p>
          {highlight.description && <p>{highlight.description}</p>}
        </div>

        {/* Render chapters if available */}
        {chapters.length > 0 && (
          <div className="schedule-chapters">
            <h2 className="section-title">Conference Chapters</h2>
            {chapters.map((chapter, chapterIdx) => (
              <div className="day-wrapper" key={chapterIdx}>
                <h2 className="day-date">{chapter.title}</h2>
                <div className="day-sessions">
                  <div className="session-card" style={{ backgroundColor: sessionColors[chapterIdx % sessionColors.length] }}>
                    <div className="session-header">
                      <h3 className="session-title">{chapter.theme}</h3>
                      <p className="session-date-time">
                        <strong>Duration:</strong> {chapter.duration}
                      </p>
                    </div>
                    <button
                      className="view-details-btn"
                      onClick={() => handleOpenSession({ 
                        ...chapter, 
                        title: chapter.theme,
                        sessionTime: chapter.duration,
                        date: chapter.duration 
                      }, sessionColors[chapterIdx % sessionColors.length])}
                    >
                      View Chapter Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Render sessions grouped by day */}
        {days.length > 0 && (
          <div className="schedule-days">
            <h2 className="section-title">Main Conference</h2>
          {days.map((day, dayIdx) => (
            <div className="day-wrapper" key={dayIdx}>
              <h2 className="day-date">{day.date}</h2>
              <div className="day-sessions">
                {day.sessions.map((session, idx) => {
                  // Compute background color based on index
                  const bgColor = sessionColors[idx % sessionColors.length];
                  return (
                    <div
                      key={idx}
                      className="session-card"
                      style={{ backgroundColor: bgColor }}
                    >
                      <div className="session-header">
                        <h3 className="session-title">{session.title}</h3>
                        {session.department && (
                          <p className="session-department">
                            {session.department}
                          </p>
                        )}
                        <p className="session-date-time">
                          {session.date} | {session.sessionTime}
                        </p>
                      </div>
                      <button
                        className="view-details-btn"
                        onClick={() => handleOpenSession(session, bgColor)}
                      >
                        View More Details
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Modal Popup for session details */}
      {selectedSession && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()} // prevent modal close on inner click
            style={{ backgroundColor: selectedSession.color }}
          >
            <button className="modal-close" onClick={handleCloseModal}>
              &times;
            </button>
            <div className="modal-header">
              <h3>{selectedSession.title}</h3>
              {selectedSession.department && (
                <p className="session-department">
                  <strong>Department:</strong> {selectedSession.department}
                </p>
              )}
              <p className="session-date-time">
                <strong>Date:</strong> {selectedSession.date} |{" "}
                <strong>Time:</strong> {selectedSession.sessionTime}
              </p>
            </div>
            <div className="modal-body">
              {selectedSession.venue && (
                <p>
                  <strong>Venue:</strong> {selectedSession.venue}
                </p>
              )}
              {selectedSession.chair && (
                <p>
                  <strong>Chair:</strong> {selectedSession.chair}
                </p>
              )}
              {selectedSession.coChair && (
                <p>
                  <strong>Co-Chair:</strong> {selectedSession.coChair}
                </p>
              )}
              {selectedSession.moderator && (
                <p>
                  <strong>Moderator:</strong> {selectedSession.moderator}
                </p>
              )}
              {selectedSession.sessions && selectedSession.sessions.length > 0 && (
                <div className="program-details">
                  <h4>Chapter Sessions</h4>
                  {selectedSession.sessions.map((session, idx) => (
                    <div key={idx} className="talk-item">
                      <p>
                        <strong>{session.title}</strong>
                        {session.department && <> - {session.department}</>}
                      </p>
                      <p>
                        <strong>Date:</strong> {session.date} | <strong>Time:</strong>{" "}
                        {session.sessionTime}
                      </p>
                      {session.venue && (
                        <p>
                          <strong>Venue:</strong> {session.venue}
                        </p>
                      )}
                      {session.chair && (
                        <p>
                          <strong>Chair:</strong> {session.chair}
                        </p>
                      )}
                      {session.coChair && (
                        <p>
                          <strong>Co-Chair:</strong> {session.coChair}
                        </p>
                      )}
                      {session.moderator && (
                        <p>
                          <strong>Moderator:</strong> {session.moderator}
                        </p>
                      )}
                      {session.program && session.program.length > 0 && (
                        <div className="program-details">
                          <h4>Session Talks</h4>
                          {session.program.map((talk, talkIdx) => (
                            <div key={talkIdx} className="talk-item">
                              <p>
                                <strong>{talk.time}</strong> - <em>{talk.talkTitle}</em>{" "}
                                {talk.speaker && (
                                  <>
                                    by <strong>{talk.speaker}</strong>
                                  </>
                                )}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {selectedSession.program && (
                <div className="program-details">
                  <h4>Session Talks</h4>
                  {selectedSession.program.map((talk, idx) => (
                    <div key={idx} className="talk-item">
                      <p>
                        <strong>{talk.time}</strong> -{" "}
                        <em>{talk.talkTitle}</em>{" "}
                        {talk.speaker && (
                          <>
                            by <strong>{talk.speaker}</strong>
                          </>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenceSchedule;
